/**
 * Custom hook for responsive media queries.
 *
 * Returns `true` when the viewport matches the given CSS media query string.
 * Listens for viewport changes and re-renders the consuming component.
 *
 * Cross-References:
 *   - Style Schema §4: Responsive Breakpoints
 *   - FR-UI-006: Mobile responsive behavior
 */
import { useState, useEffect } from 'react';

/**
 * @param query - A CSS media query, e.g. `"(max-width: 768px)"`.
 * @returns Whether the query currently matches.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    setMatches(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

/** Breakpoint helpers matching tokens.css / style-schema §4. */
export const BREAKPOINTS = {
  mobile: '(max-width: 768px)',
  tablet: '(min-width: 769px) and (max-width: 1024px)',
  desktop: '(min-width: 1025px)',
} as const;
